import axios, { AxiosResponse } from "axios";
import { SoftwareCreate } from "../components/models/Softwares/SoftwareCreate";
import { CompanyCreate } from "../components/models/Companies/CompanyCreate";
import { UserCreate } from "../components/models/Users/UserCreate";
import { SubscriptionCreate } from "../components/models/Subscription/SubscriptionCreate";
import { LicenseCreate } from "../components/models/Licenses/LicenseCreate";
import { LoginCredential } from "../components/models/Login/LoginCredential";
import PasswordChange from "../components/models/Users/PasswordChange";
import { RegisterCredential } from "../components/models/Login/RegisterCredential";
import ResetPassword from "../components/models/Users/ResetPassword";
import { OfflineLicense } from "../components/models/Licenses/OfflineLicense";

const sleep = (delay: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
};

// Backend API URL (local pc)
// axios.defaults.baseURL = "http://localhost:5000/api";

// Backend API URL (Azure hosting)
axios.defaults.baseURL = "https://licback.uasengineers.com/api";

axios.interceptors.response.use(async (response) => {
  try {
    await sleep(1000);
    return response;
  } catch (error) {
    console.log(error);
    return await Promise.reject(error);
  }
});

const responseBody = (response: AxiosResponse) => response.data;

//set the CRUD process that need to be use in below
const request = {
  get: (url: string, token: string) =>
    axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(responseBody),
  post: (url: string, body: {}, token: string) =>
    axios
      .post(url, body, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(responseBody),
  put: (url: string, body: {}, token: string) =>
    axios
      .put(url, body, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(responseBody),
  del: (url: string, token: string) =>
    axios
      .delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(responseBody),
};

// included the endpoint connection in each controllers, will be use in UI tsx file
const Softwares = {
  list: (token: string) => request.get("/Software", token),
  create: (software: SoftwareCreate, token: string) =>
    request.post("/Software", software, token),
  update: (softwareId: number, software: SoftwareCreate, token: string) =>
    request.put(`/Software/${softwareId}`, software, token),
  delete: (softwareId: number, token: string) =>
    request.del(`/Software/${softwareId}`, token),
  exportToCSV: (token: string) =>
    axios.get("/Software/export", {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
};

const Company = {
  list: (token: string) => request.get("/Company", token),
  getCompanyDetail: (companyId: number, token: string) =>
    request.get(`/Company/${companyId}`, token),
  create: (company: CompanyCreate, token: string) =>
    request.post("/Company", company, token),
  update: (companyId: number, company: CompanyCreate, token: string) =>
    request.put(`/Company/${companyId}`, company, token),
  delete: (companyId: number, token: string) =>
    request.del(`/Company/${companyId}`, token),
  exportToCSV: (token: string) =>
    axios.get("/Company/export", {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
};

const User = {
  list: (token: string) => request.get("/User", token),
  getUserByCompanyId: (companyId: number, token: string) =>
    request.get(`/User/company/${companyId}`, token),
  getUserDetail: (userId: number, token: string) =>
    request.get(`/User/userDetails/${userId}`, token),
  create: (user: UserCreate, token: string) =>
    request.post("/User", user, token),
  update: (userId: number, user: UserCreate, token: string) =>
    request.put(`/User/${userId}`, user, token),
  delete: (userId: number, token: string) =>
    request.del(`/User/${userId}`, token),
  resetPassword: (email: string, user: ResetPassword, token: string) =>
    request.put(`/User/resetPassword/${email}`, user, token),
  exportToCSV: (token: string) =>
    axios.get("/User/export", {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
};

const Subscription = {
  list: (token: string) => request.get("/Subscription", token),
  getSubscriptionByCompanyId: (companyId: number, token: string) =>
    request.get(`/Subscription/${companyId}`, token),
  create: (subscription: SubscriptionCreate, token: string) =>
    request.post("/Subscription", subscription, token),
  update: (
    subscriptionId: number,
    subscription: SubscriptionCreate,
    token: string,
  ) => request.put(`/Subscription/${subscriptionId}`, subscription, token),
  delete: (subscriptionId: number, token: string) =>
    request.del(`/Subscription/${subscriptionId}`, token),
  exportToCSV: (token: string) =>
    axios.get("/Subscription/export", {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
};

const License = {
  list: (token: string) => request.get("/License", token),
  getLicenseBySubscription: (subscriptionId: number, token: string) =>
    request.get(`/License/bySubscriptionId/${subscriptionId}`, token),
  getLicenseByUser: (userId: number, token: string) =>
    request.get(`/License/byUserId/${userId}`, token),
  update: (licenseId: number, license: LicenseCreate, token: string) =>
    request.put(`/License/${licenseId}`, license, token),
  delete: (licenseId: number, token: string) =>
    request.del(`/License/${licenseId}`, token),
  clearLinkedPc: (licenseKey: string, token: string) =>
    axios.post(`/License/ClearLinkedPC/${licenseKey}`, null, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }),
  // To Link user to the license
  assignUserToLic: (
    licenseKey: string,
    userId: number | undefined,
    token: string,
  ) =>
    request.post(
      `/License/AssignUser/${licenseKey}/${userId ?? ""}`,
      {},
      token,
    ),
  // To generate new License
  generateNewLicense: (licenseGenerate: LicenseCreate, token: string) =>
    axios
      .post("/License/generate", licenseGenerate, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data),
  // To redownload License(existing license)
  downloadLicense: (licenseKey: string, token: string) =>
    axios.get(`/License/download/${licenseKey}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  // To download temporary Offline license
  downloadOfflineLicense: (offlinelicenseData: OfflineLicense, token: string) =>
    axios.post(`/License/GenerateOfflineLicense/`, offlinelicenseData, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  // Check user conflict when assign user to license
  checkUserConflict: (subscriptionId: number, userId: number, token: string) =>
    request.get(
      `/License/checkUserConflict/${subscriptionId}/${userId}`,
      token,
    ),
};

const Authentication = {
  login: (credentials: LoginCredential) =>
    axios.post("Auth/login", credentials),
  accountActivation: (credentials: RegisterCredential) =>
    axios.post<{ token: string }>("Auth/register", credentials),
  resetPassword: (
    userId: number,
    passwordData: PasswordChange,
    token: string,
  ) => request.post(`Auth/resetPassword/${userId}`, passwordData, token),
};

const ChangeLog = {
  list: (token: string) => request.get("/ChangeLog", token),
  exportToCSV: (token: string) =>
    axios.get("/ChangeLog/export", {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone, // "America/New_York" or "Asia/Kuala_Lumpur"
      },
    }),
};

const DatabaseExportService = {
  listBackupFiles: (token: string) =>
    request.get("/DatabaseMaintenance/list-backups", token),
  exportToBacpac: (token: string) =>
    request.post("/DatabaseMaintenance/export-bacpac", {}, token),
  downloadbackup: (fileName: string, token: string) =>
    axios.get(`/DatabaseMaintenance/download-backup/${fileName}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  deleteBackup: (fileName: string, token: string) =>
    axios.delete(`/DatabaseMaintenance/delete-backup/${fileName}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
};

//store the Groups for CRUD operation to endPoint
const agent = {
  Softwares,
  Company,
  User,
  Subscription,
  License,
  Authentication,
  ChangeLog,
  DatabaseExportService,
};

export default agent;
